// src/pages/ProductsList.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { productService } from '../services/api';
import QuickViewModal from '../components/Products/QuickViewModal';
import './ProductsList.css';

const ProductsList = () => {
  const { category, brand, type } = useParams();

  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const queryParams = new URLSearchParams(location.search);
  
  // Product state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Display controls
  const [displayCount, setDisplayCount] = useState(12);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  

  // Quick View Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  // Brand lookup for slug-to-ID conversion
  const [brandLookup, setBrandLookup] = useState({});


  // Fetch brands for slug-to-ID lookup on component mount
  useEffect(() => {
    fetchBrands();
  }, []);


  // Fetch products based on URL parameters
  useEffect(() => {
    fetchProducts();
  }, [category, brand, type, location.search]);

  // Fetch brands for slug-to-ID lookup
  const fetchBrands = async () => {
    try {
      console.log('🔄 Fetching brands for slug-to-ID lookup...');
      
      const response = await fetch('https://kingsolarman.pythonanywhere.com/api/brands/');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Brands fetched successfully:', data);
      
      // Build slug-to-ID lookup map
      const lookup = {};
      const brands = data.results || data;
      
      brands.forEach(brand => {
        if (brand.slug && brand.id) {
          lookup[brand.slug.toLowerCase()] = brand.id;
        }
      });
      
      console.log('📋 Brand lookup map created:', lookup);
      setBrandLookup(lookup);
      
    } catch (error) {
      console.error('❌ Error fetching brands:', error);
      // Continue without brand lookup - brands won't be filtered by slug
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      

      // Get parameters from URL query string (NOT from route params)
      const categoryParam = queryParams.get('category');
      const brandParam = queryParams.get('brand');
      const searchParam = queryParams.get('search');
      
      // Build API query parameters
      const query = {
        page: 1,
        page_size: 100 // Get enough for display
      };
      

      // Only add parameters if they exist and ensure they are strings
      if (categoryParam) {
        // Ensure categoryParam is a string (handle array case)
        const categoryStr = Array.isArray(categoryParam) ? categoryParam[0] : categoryParam;
        

        // Convert URL-friendly category names to Django API category IDs
        const categoryIdMap = {
          'solar-inverters': 1,
          'solar-panels': 3,
          'batteries': 2,
          'accessories': 4,
          'mppt_controllers': 5,
          'solar_kits': 6,
          'inverters': 1,
          'panels': 3,
          'solar_inverters': 1,
          'solar_panels': 3
        };
        
        // Use mapped ID or try to parse as number, fallback to category name
        const categoryId = categoryIdMap[categoryStr] || parseInt(categoryStr, 10);
        query.category = isNaN(categoryId) ? categoryStr : categoryId;
      }
      

      if (brandParam) {
        // Ensure brandParam is a string (handle array case)
        const brandStr = Array.isArray(brandParam) ? brandParam[0] : brandParam;
        
        // Convert brand slug to API ID using lookup map
        const brandId = brandLookup[brandStr.toLowerCase()];
        query.brand = brandId || brandStr; // Use ID if found, fallback to slug
        
        console.log(`🏷️ Brand filtering: "${brandStr}" → ${brandId ? `ID ${brandId}` : 'falling back to slug'}`);
      }
      
      if (searchParam) {
        query.search = searchParam;
      }

      console.log('🔍 Fetching products with query:', query);
      
      const response = await productService.getProducts(query);
      console.log('📦 API Response:', response);
      
      if (response && response.results) {
        const processedProducts = response.results.map(processProductData);
        setProducts(processedProducts);
        setTotalProducts(response.count || processedProducts.length);
      } else {
        console.warn('⚠️ No products in response:', response);
        setProducts([]);
        setTotalProducts(0);
      }
    } catch (err) {
      console.error('❌ Error fetching products:', err);
      setError(err.message || 'Failed to load products. Please try again.');
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  const processProductData = (apiProduct) => {
    // Based on your API response structure
    const productData = {
      id: apiProduct.id,
      name: apiProduct.name,
      productname: apiProduct.name,
      sku: apiProduct.sku,
      brand: apiProduct.brand?.name || 'Unknown Brand',
      category: apiProduct.category || 'uncategorized',
      current_price: parseFloat(apiProduct.current_price) || 0,
      has_discount: apiProduct.has_discount || false,
      discount_percentage: apiProduct.discount_percentage || 0,
      stock_status: apiProduct.stock_status || 'out_of_stock',
      primary_image: apiProduct.primary_image || null,
      // Try to get additional fields if they exist
      description: apiProduct.description || '',
      warranty: apiProduct.warranty || '',
      power: apiProduct.power || '',
      voltage: apiProduct.voltage || '',
      capacity: apiProduct.capacity || '',
      model: apiProduct.model || '',
      dimensions: apiProduct.dimensions || '',
      weight: apiProduct.weight || ''
    };

    // Calculate prices based on discount
    const currentPrice = productData.current_price;
    const hasDiscount = productData.has_discount;
    const discountPercentage = productData.discount_percentage;
    
    let originalPrice = currentPrice;
    if (hasDiscount && discountPercentage > 0) {
      originalPrice = currentPrice / (1 - discountPercentage / 100);
    }

    return {
      ...productData,
      price: originalPrice, // Original price
      discountprice: hasDiscount ? currentPrice : originalPrice, // Discounted price or original
      stock: productData.stock_status === 'in_stock' ? 'In Stock' : 'Out of Stock',
      productinfo: productData.description || '',
      image: productData.primary_image || '/images/placeholder.jpg',
      rating: 0,
      reviews: 0
    };
  };

  // Apply sorting
  const getSortedProducts = () => {
    let sorted = [...products];
    
    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => (a.discountprice || a.current_price) - (b.discountprice || b.current_price));
      case 'price-high':
        return sorted.sort((a, b) => (b.discountprice || b.current_price) - (a.discountprice || a.current_price));
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'brand':
        return sorted.sort((a, b) => a.brand.localeCompare(b.brand));
      case 'discount':
        return sorted.sort((a, b) => (b.discount_percentage || 0) - (a.discount_percentage || 0));
      case 'newest':
      default:
        return sorted.sort((a, b) => b.id - a.id); // Assuming higher ID = newer
    }
  };

  // Get displayed products
  const sortedProducts = getSortedProducts();
  const displayedProducts = sortedProducts.slice(0, displayCount);

  // Handlers
  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };


  const handleAddToCart = (product) => {
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: product.discountprice || product.current_price,
      originalPrice: product.price,
      image: product.image,
      brand: typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : (typeof product.brand === 'string' ? product.brand : 'Unknown Brand'),
      description: product.description,
      sku: product.sku,
      category: typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : (typeof product.category === 'string' ? product.category : 'Unknown Category'),
      quantity: 1
    };

    addToCart(cartProduct);
    alert(`${cartProduct.name} added to cart!`);
  };

  const handleQuickView = (product) => {
    setQuickViewProduct(product);
    setIsModalOpen(true);
  };


  const closeQuickView = () => {
    setIsModalOpen(false);
    setQuickViewProduct(null);
  };


  // ✅ ADD WISHLIST HANDLER
  const handleWishlistToggle = (product, e) => {
    e.stopPropagation(); // Prevent product card click
    
    const wishlistProduct = {
      id: product.id,
      name: product.name,
      price: product.discountprice || product.current_price,
      originalPrice: product.price,
      image: product.image,
      brand: typeof product.brand === 'object' ? (product.brand.display_name || product.brand.name || 'Unknown Brand') : (typeof product.brand === 'string' ? product.brand : 'Unknown Brand'),
      description: product.description,
      sku: product.sku,
      category: typeof product.category === 'object' ? (product.category.display_name || product.category.name || 'Unknown Category') : (typeof product.category === 'string' ? product.category : 'Unknown Category')
    };

    toggleWishlist(wishlistProduct);
    
    // Show feedback
    const isCurrentlyInWishlist = isInWishlist(product.id);
    const message = isCurrentlyInWishlist 
      ? `${wishlistProduct.name} removed from wishlist!`
      : `${wishlistProduct.name} added to wishlist!`;
    
    // Simple visual feedback
    alert(message);
  };

  const formatPrice = (price) => {
    if (!price || isNaN(price)) return 'P 0.00';
    return `P ${parseFloat(price).toFixed(2)}`;
  };

  const formatImageUrl = (imageUrl) => {
    if (!imageUrl) return '/images/placeholder.jpg';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `/images/${imageUrl}`;
  };

  const getPageTitle = () => {
    const categoryParam = queryParams.get('category');
    const brandParam = queryParams.get('brand');
    const searchParam = queryParams.get('search');
    
    if (categoryParam) {
      const categoryNames = {
        'inverters': 'Solar Inverters',
        'panels': 'Solar Panels',
        'batteries': 'Batteries',
        'accessories': 'Accessories',
        'solar_inverters': 'Solar Inverters',
        'solar_panels': 'Solar Panels'
      };
      return `${categoryNames[categoryParam] || categoryParam.replace('_', ' ').toUpperCase()} Products`;
    }
    
    if (brandParam) return `${brandParam} Products`;
    if (searchParam) return `Search: ${searchParam}`;
    return 'All Products';
  };



  const getCategoryDisplayName = (category) => {
    if (!category) return '';
    const categoryNames = {
      '1': 'Solar Inverters',
      '2': 'Batteries',
      '3': 'Solar Panels',
      '4': 'Accessories',
      '5': 'MPPT Controllers',
      '6': 'solar_kits',
      'solar-inverters': 'Solar Inverters',
      'solar-panels': 'Solar Panels',
      'batteries': 'Batteries',
      'accessories': 'Accessories',
      'mppt_controllers': 'mppt_controllers',
      'solar_kits': 'solar_kits',
      'solar_inverters': 'Solar Inverters',
      'solar_panels': 'Solar Panels'
    };
    return categoryNames[category] || category.replace('_', ' ').toUpperCase();
  };

  const getBreadcrumbCategory = () => {
    const categoryParam = queryParams.get('category');
    if (!categoryParam) return null;
    
    const categoryNames = {
      'inverters': 'Solar Inverters',
      'panels': 'Solar Panels',
      'batteries': 'Batteries',
      'accessories': 'Accessories'
    };
    
    return categoryNames[categoryParam] || categoryParam;
  };

  const getBreadcrumbBrand = () => {
    const brandParam = queryParams.get('brand');
    if (!brandParam) return null;
    
    // Convert brand slug to display name
    const brandNames = {
      'deye': 'Deye',
      'dyness': 'Dyness',
      'freedomwon': 'FreedomWon',
      'fronius': 'Fronius',
      'jinko': 'Jinko',
      'longi': 'Longi',
      'luxpower': 'Luxpower',
      'mersen': 'Mersen',
      'leader': 'Leader',
      'renusol': 'Renusol'
    };
    
    return brandNames[brandParam.toLowerCase()] || brandParam;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <h2>Error Loading Products</h2>
        <p className="error-message">{error}</p>
        <div className="error-suggestions">
          <p>Please try:</p>
          <ul>
            <li>Clicking a different category</li>
            <li>Viewing all products</li>
            <li>Refreshing the page</li>
          </ul>
        </div>
        <div className="error-actions">
          <button onClick={fetchProducts} className="retry-btn">
            ↻ Try Again
          </button>
          <Link to="/products" className="back-btn">
            ← View All Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="products-list-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Home</Link> &gt;
        <Link to="/products">Products</Link>
        {getBreadcrumbCategory() && (
          <>
            &gt; <span className="current-page">{getBreadcrumbCategory()}</span>
          </>
        )}
        {getBreadcrumbBrand() && (
          <>
            &gt; <span className="current-page">{getBreadcrumbBrand()}</span>
          </>
        )}
      </div>

      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">{getPageTitle()}</h1>
        <p className="page-subtitle">
          {totalProducts} {totalProducts === 1 ? 'product' : 'products'} found
          {queryParams.get('category') && ` in ${getBreadcrumbCategory()}`}
          {queryParams.get('brand') && ` from ${getBreadcrumbBrand()}`}
        </p>
      </div>

      {/* Products Section */}
      <section className="product-catalog-home">
        <div className="container">
          {/* Results Header */}
          <div className="results-header">
            <div className="results-info">
              <span>
                Showing {Math.min(displayCount, displayedProducts.length)} of {totalProducts} products
              </span>
            </div>
            <div className="results-controls">
              <div className="control-group">
                <label>Display:</label>
                <select
                  value={displayCount}
                  onChange={(e) => {
                    setDisplayCount(parseInt(e.target.value, 10));
                  }}
                  className="control-select"
                >
                  <option value={12}>12 per page</option>
                  <option value={24}>24 per page</option>
                  <option value={36}>36 per page</option>
                  <option value={48}>48 per page</option>
                </select>
              </div>
              <div className="control-group">
                <label>Sort by:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="control-select">
                  <option value="newest">Newest</option>
                  <option value="price-low">Price, low to high</option>
                  <option value="price-high">Price, high to low</option>
                  <option value="name">Name, A-Z</option>
                  <option value="brand">Brand</option>
                  <option value="discount">Best Discount</option>
                </select>
              </div>
              <div className="view-controls">
                <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>List</button>
                <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>Grid</button>
              </div>
            </div>
          </div>

          {/* Products Grid/List */}
          {displayedProducts.length === 0 ? (
            <div className="no-products">
              <p>No products found matching your criteria.</p>
              <Link to="/products" className="back-to-all">
                View All Products
              </Link>
            </div>
          ) : (
            <div className={`products-container ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
              {displayedProducts.map(product => {
                const productId = product.id;
                const productName = product.name || 'Product Name Not Available';

                const productBrand = typeof product.brand === 'object' 
                  ? (product.brand.display_name || product.brand.name || 'Unknown Brand') 
                  : (typeof product.brand === 'string' ? product.brand : 'Unknown Brand');
                const productPrice = product.price || product.current_price || 0;
                const productDiscountPrice = product.discountprice || product.current_price || 0;
                const productStock = product.stock || 'Out of Stock';

                const productCategory = typeof product.category === 'object' 
                  ? (product.category.display_name || product.category.name || 'uncategorized') 
                  : (typeof product.category === 'string' ? product.category : 'uncategorized');
                const productImage = product.image;
                
                const hasDiscount = product.has_discount;
                const discountPercentage = product.discount_percentage || 0;
                
                return (
                  <div key={productId} className={`product-card ${viewMode === 'list' ? 'list-card' : 'grid-card'}`}>
                    <div className="product-image" style={{ background: 'transparent' }}>
                      {productImage ? (
                        <img
                          src={formatImageUrl(productImage)}
                          alt={productName}
                          className="product-img"
                          onClick={() => handleProductClick(productId)}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            cursor: 'pointer'
                          }}
                          onError={(e) => { 
                            e.target.onerror = null; 
                            e.target.src = '/images/placeholder.jpg';
                          }}
                        />
                      ) : (
                        <div 
                          className="image-placeholder"
                          onClick={() => handleProductClick(productId)}
                          style={{ cursor: 'pointer' }}
                        >
                          {productBrand}
                        </div>
                      )}
                      <button 
                        className="quick-view-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickView(product);
                        }}
                      >
                        Quick view
                      </button>
                      {hasDiscount && discountPercentage > 0 && (
                        <div className="product-badge">
                          <span className="discount-badge">-{discountPercentage}%</span>
                        </div>
                      )}
                    </div>

                    <div className="product-info">
                      {/* <div className="product-brand">{productBrand}</div> */}
                      <h3 
                        className="product-name" 
                        title={productName}
                        onClick={() => handleProductClick(productId)}
                        style={{ 
                          cursor: 'pointer',
                          ...(viewMode === 'list' ? { 
                            backgroundColor: 'inherit', 
                            color: 'black',
                            padding: '0px 2px 20px 0px',
                          } : {})
                        }}
                      >
                        {productName}
                      </h3>

                      {viewMode === 'list' && product.description && (
                        <p className="product-description">
                          {product.description.length > 150 ? product.description.substring(0, 150) + '...' : product.description}
                        </p>
                      )}

                      <div className="product-pricing">
                        <span className="current-price">
                          {formatPrice(productDiscountPrice)}
                        </span>
                        {hasDiscount && product.price > productDiscountPrice && (
                          <span className="original-price">{formatPrice(product.price)}</span>
                        )}
                      </div>

                      <div className="stock-info">
                        <span className={`${productStock && productStock.toLowerCase().includes('out') ? 'out-of-stock' : 'in-stock'}`}>
                          {productStock}
                        </span>
                        {/* <span className="product-category">{getCategoryDisplayName(productCategory)}</span> */}
                      </div>


                      <div className="product-actions">
                        <button 
                          className="add-to-cart-btn" 
                          onClick={() => handleAddToCart(product)}
                          disabled={productStock && productStock.toLowerCase().includes('out')}
                        >
                          {productStock && productStock.toLowerCase().includes('out') ? 'Out of Stock' : 'Add to cart'}
                        </button>
                        <button 
                          className="wishlist-btn" 
                          onClick={(e) => handleWishlistToggle(product, e)}
                          style={{
                            color: isInWishlist(productId) ? '#e74c3c' : '#666',
                            background: isInWishlist(productId) ? '#ffe6e6' : 'transparent',
                            border: isInWishlist(productId) ? '1px solid #e74c3c' : '1px solid #ddd'
                          }}
                        >
                          {isInWishlist(productId) ? '❤️' : '🤍'} {isInWishlist(productId) ? 'In Wishlist' : 'Wishlist'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Quick View Modal */}
          {isModalOpen && quickViewProduct && (
            <QuickViewModal 
              product={quickViewProduct} 
              isOpen={isModalOpen} 
              onClose={closeQuickView} 
              onAddToCart={handleAddToCart} 
              formatPrice={formatPrice} 
            />
          )}

          {/* Load More Button */}
          {totalProducts > displayCount && (
            <div className="pagination">
              <button className="load-more-btn" onClick={() => setDisplayCount(displayCount + 12)}>Load More Products</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProductsList;